import React from 'react';
import { Link } from 'react-router-dom';
import { CaraLoomLogo } from './CaraLoomLogo';
import { MarketplaceDisclaimer } from './MarketplaceDisclaimer';
import { ShieldCheck, Lock, BadgeCheck, Mail, MapPin } from 'lucide-react';
import { EMAIL, NAP } from '../../lib/siteConfig';

/**
 * Site-wide footer — Feb 2026 "one continuous thread" narrative.
 *
 * The footer mirrors the landing's audience-first storytelling: three
 * columns for the three strands (Hospitals · Clinicians · Families)
 * each pointing at the right join path, plus a Company column for
 * legal/trust/contact. Every CTA leads to a LIVE destination — no
 * V1-archived placeholder routes.
 */
export const Footer = () => {
  const year = new Date().getFullYear();

  const columns = [
    // Fix-Now Part 2 §4 (Jul 2026) — footer restricted to live-focus
    // destinations only. De-linked (routes preserved): /about, /customers,
    // /platform-onepager, /status, /trust-safety, /#thread. Backed by the
    // footer-link lint in backend/tests/test_phase7_public_surface.py.
    {
      heading: 'Platform',
      links: [
        { label: 'Home',                   to: '/' },
        { label: 'For Discharge Planners', to: '/for-discharge-planners' },
        { label: 'For Families',           to: '/for-families' },
        { label: 'Pricing',                to: '/pricing' },
      ],
    },
    {
      heading: 'Get started',
      links: [
        { label: 'Book a pilot',            to: '/auth?role=discharge_planner' },
        { label: 'Join as a Provider',      to: '/auth?role=agency' },
        { label: 'Find care for my family', to: '/auth?role=patient' },
        { label: 'Sign in',                 to: '/auth' },
      ],
    },
    {
      heading: 'Company',
      links: [
        { label: 'Trust portal', to: '/trust' },
        { label: 'Contact',      to: '/contact' },
        { label: 'Privacy',      to: '/privacy' },
        { label: 'Terms',        to: '/terms' },
      ],
    },
  ];

  return (
    <footer className="bg-cn-bg border-t border-cn-border mt-24" data-testid="site-footer">
      <div className="cn-container py-16">
        {/* Top: brand + columns */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2 md:col-span-2">
            <CaraLoomLogo />
            <p className="mt-4 text-sm text-cn-text-muted leading-relaxed max-w-xs"
                data-testid="footer-tagline">
              Care, woven into one continuous thread. From discharge to
              accepted placement to first visit — one record, one promise.
            </p>
            <div className="mt-5 space-y-2">
              <a href={`mailto:${EMAIL}`}
                  className="flex items-center gap-2 text-sm text-cn-text hover:text-cn-primary"
                  data-testid="footer-email">
                <Mail className="w-4 h-4" /> {EMAIL}
              </a>
              <p className="flex items-center gap-2 text-sm text-cn-text-muted"
                  data-testid="footer-address">
                <MapPin className="w-4 h-4" /> {NAP.addressLocality}, {NAP.addressRegion} {NAP.postalCode} · Serving providers nationwide
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="cn-trust-badge"><ShieldCheck className="w-3.5 h-3.5" /> HIPAA-ready</span>
              <span className="cn-trust-badge"><Lock className="w-3.5 h-3.5" /> End-to-end encrypted</span>
              <span className="cn-trust-badge"><BadgeCheck className="w-3.5 h-3.5" /> Background-checked</span>
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.heading}
                  data-testid={`footer-col-${col.heading.toLowerCase().replace(/\s+/g, '-')}`}>
              <h4 className="cn-eyebrow mb-4">{col.heading}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      target={l.newTab ? '_blank' : undefined}
                      rel={l.newTab ? 'noopener noreferrer' : undefined}
                      className="text-sm text-cn-text hover:text-cn-primary transition-colors"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Disclaimer banner */}
        <div className="mt-12">
          <MarketplaceDisclaimer variant="banner" />
        </div>

        {/* Full legal block */}
        <div className="mt-8 pt-8 border-t border-cn-border">
          <MarketplaceDisclaimer variant="full" />
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-8 border-t border-cn-border flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <p className="text-xs text-cn-text-muted">
            © {year} CaraLoom, Inc. All rights reserved. Care, woven into one continuous thread.
          </p>
          <div className="flex gap-5 text-xs text-cn-text-muted">
            <Link to="/privacy" className="hover:text-cn-primary">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-cn-primary">Terms of Service</Link>
            <Link to="/trust" className="hover:text-cn-primary">Trust</Link>
            <Link to="/contact" className="hover:text-cn-primary">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
